import { Moon, Sun } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useTheme } from './ThemeProvider';
import { cn } from '@/lib/cn';

/**
 * Theme switch.
 *
 * It is a real <button> with aria-pressed, not a div with a click handler, so it is in the
 * tab order, responds to space and enter, and is announced with its state. The icon is
 * aria-hidden because the accessible name already carries the meaning.
 */
export function ThemeToggle({ className }: { readonly className?: string }): React.JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const nextLabel = theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={nextLabel}
          aria-pressed={theme === 'dark'}
          className={cn(
            'inline-flex size-9 items-center justify-center rounded-[var(--radius-control)]',
            'border border-border bg-surface text-fg-muted',
            'transition-colors hover:bg-surface-hover hover:text-fg',
            className,
          )}
        >
          {theme === 'dark' ? (
            <Moon className="size-4" aria-hidden="true" />
          ) : (
            <Sun className="size-4" aria-hidden="true" />
          )}
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          sideOffset={6}
          className="animate-pop-in z-50 rounded-md border border-border bg-surface-raised px-2 py-1 text-xs text-fg shadow-md"
        >
          {nextLabel}
          <Tooltip.Arrow className="fill-[var(--color-surface-raised)]" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
